import { announce } from "@kovernow/flows/announce";
import { WifiOff } from "lucide-react";
import { useEffect, useRef } from "react";
import { useLastUpdated, useOnline, useServices } from "../core/app-context";
import { useT } from "../core/use-t";

/**
 * Offline (CAP-4): a banner, announced assertively once per offline spell,
 * with the time content was last updated. Mutating actions read
 * useMutationGate() and become aria-disabled with a reason.
 */
export function OfflineBanner() {
  const online = useOnline();
  const lastUpdated = useLastUpdated();
  const { format } = useServices();
  const t = useT();
  const announced = useRef(false);

  useEffect(() => {
    if (!online && !announced.current) {
      announced.current = true;
      announce(t("offline.banner"), "assertive");
    }
    if (online) announced.current = false;
  }, [online, t]);

  if (online) return null;
  return (
    <div
      data-testid="offline-banner"
      className="flex items-start gap-3 border-b border-status-warning bg-muted px-4 py-3 text-sm text-foreground"
    >
      <WifiOff aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-status-warning" />
      <div>
        <p className="font-semibold">{t("offline.banner")}</p>
        <p className="text-muted-foreground">
          {lastUpdated
            ? t("common.lastUpdated", { time: format.relative(lastUpdated) })
            : t("offline.neverUpdated")}
        </p>
      </div>
    </div>
  );
}
